import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMiroAnnotations, MiroPageId } from '@/hooks/useMiroAnnotations';
import { MiroAnnotationCard } from './MiroAnnotationCard';
import { MiroArrow } from './MiroArrow';

/**
 * Définition d'une annotation individuelle
 */
export interface AnnotationDef {
  /** Identifiant unique de l'annotation */
  id: string;
  /** Sélecteur CSS ciblant l'élément de l'UI (ex: "[data-miro-target='planning-panel']") */
  targetSelector: string;
  /** Texte affiché dans le cadre */
  text: string;
  /** Position du cadre par rapport à l'élément ciblé */
  position: 'left' | 'right' | 'top' | 'bottom';
  /** Décalage horizontal du cadre en px (positif = vers la droite) */
  offsetX?: number;
  /** Décalage vertical du cadre en px (positif = vers le bas) */
  offsetY?: number;
}

/**
 * Position calculée d'un cadre d'annotation (en px, fixed)
 */
export interface CardPosition {
  left: number;
  top: number;
}

interface MiroAnnotationsProps {
  pageId: MiroPageId;
  annotations: AnnotationDef[];
}

const CARD_WIDTH = 220;
const CARD_HEIGHT = 90; // estimation pour le centrage vertical

/**
 * Calcule la position fixe du cadre d'annotation
 * en fonction de la position de la cible et des offsets.
 */
export function calcCardPosition(
  targetRect: DOMRect,
  position: AnnotationDef['position'],
  offsetX: number,
  offsetY: number
): CardPosition {
  switch (position) {
    case 'left':
      return {
        left: targetRect.left - CARD_WIDTH + offsetX,
        top: targetRect.top + targetRect.height / 2 - CARD_HEIGHT / 2 + offsetY,
      };
    case 'right':
      return {
        left: targetRect.right + offsetX,
        top: targetRect.top + targetRect.height / 2 - CARD_HEIGHT / 2 + offsetY,
      };
    case 'top':
      return {
        left: targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2 + offsetX,
        top: targetRect.top - CARD_HEIGHT + offsetY,
      };
    case 'bottom':
    default:
      return {
        left: targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2 + offsetX,
        top: targetRect.bottom + offsetY,
      };
  }
}

/**
 * Système d'annotations style Miro.
 * S'affiche UNE SEULE FOIS par page (localStorage), non-bloquant.
 * - Pas d'overlay
 * - Pas de blur
 * - Flèches SVG courbes entre les cadres et les éléments ciblés
 * - Fermeture au clic n'importe où (sauf sur les cadres eux-mêmes)
 */
export function MiroAnnotations({ pageId, annotations }: MiroAnnotationsProps) {
  const { isVisible, isReady, dismiss } = useMiroAnnotations(pageId);
  const [targetRects, setTargetRects] = useState<Map<string, DOMRect>>(new Map());

  // Calcul des positions des éléments ciblés dans le DOM
  useEffect(() => {
    if (!isReady) return;

    const calcPositions = () => {
      const map = new Map<string, DOMRect>();
      annotations.forEach(ann => {
        const el = document.querySelector(ann.targetSelector);
        if (el) {
          map.set(ann.id, el.getBoundingClientRect());
        }
      });
      setTargetRects(new Map(map));
    };

    calcPositions();

    // Recalculer si la fenêtre est redimensionnée
    const observer = new ResizeObserver(calcPositions);
    observer.observe(document.body);

    // Recalculer au scroll (position fixed = relative au viewport, pas au document)
    window.addEventListener('scroll', calcPositions, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', calcPositions);
    };
  }, [isReady, annotations]);

  // Fermer au clic n'importe où sur la page, SAUF sur les cadres
  // IMPORTANT : ne pas appeler stopPropagation ni preventDefault — le clic se propage normalement
  useEffect(() => {
    if (!isVisible) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Si le clic est sur un cadre annotation → ne pas fermer
      if (target.closest('[data-miro-card]')) return;
      // Sinon fermer (sans bloquer le clic)
      dismiss();
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isVisible, dismiss]);

  // Ne pas afficher sur mobile / tablette
  if (typeof window !== 'undefined' && window.innerWidth <= 1024) return null;

  if (!isVisible) return null;

  return createPortal(
    <>
      {/* SVG overlay pour toutes les flèches — pointer-events: none pour ne pas bloquer la page */}
      <svg
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 60,
          overflow: 'visible',
        }}
      >
        {annotations.map(ann => {
          const targetRect = targetRects.get(ann.id);
          if (!targetRect) return null;
          const cardPos = calcCardPosition(
            targetRect,
            ann.position,
            ann.offsetX ?? -20,
            ann.offsetY ?? 0
          );
          return (
            <MiroArrow
              key={ann.id}
              targetRect={targetRect}
              cardPos={cardPos}
              cardWidth={CARD_WIDTH}
              cardHeight={CARD_HEIGHT}
              position={ann.position}
            />
          );
        })}
      </svg>

      {/* Cadres d'annotation */}
      {annotations.map((ann, i) => {
        const targetRect = targetRects.get(ann.id);
        if (!targetRect) return null;
        const cardPos = calcCardPosition(
          targetRect,
          ann.position,
          ann.offsetX ?? -20,
          ann.offsetY ?? 0
        );
        return (
          <MiroAnnotationCard
            key={ann.id}
            index={i + 1}
            text={ann.text}
            cardPos={cardPos}
            cardWidth={CARD_WIDTH}
            onClose={dismiss}
          />
        );
      })}
    </>,
    document.body
  );
}
